import { useState, useRef } from "react";
import { toPng } from "html-to-image";
import { toSvg } from "../utilities/toSvg";
import {
  stringifyVariables,
  stringifyDimensions,
  stringifyTimestamp,
} from "../utilities/stringify";

import { ratios } from "../settings";
import patterns from "../patterns";

import Actions from "../components/Actions";
import Canvas from "../components/Canvas";
import Dimensions from "../components/Dimensions";
import Inputs from "../components/Inputs";
import Patterns from "../components/Patterns";
import Presets from "../components/Presets";

import "./App.css";

const App = () => {
  const [iteration, setIteration] = useState(0);
  const recordingRef = useRef(null);
  const [exportState, setExportState] = useState(null);
  const [pattern, setPattern] = useState(patterns[0]);
  const [presets, setPresets] = useState(pattern.presets);
  const [preset, setPreset] = useState(presets[0]);
  const [inputs, setInputs] = useState(pattern.variables);
  const [variables, setVariables] = useState({
    ...pattern.variables.reduce(
      (object, item) => ({
        ...object,
        [item["name"]]: preset[item["name"]],
      }),
      {},
    ),
  });
  const [dimensions, setDimensions] = useState({
    height: ratios[0].height,
    width: ratios[0].width,
    ratio: ratios[0].name,
  });

  const handlePatternChange = (event) => {
    const pattern = patterns.find(
      (pattern) => pattern.name === event.target.value,
    );
    setPattern(pattern);
    setPresets(pattern.presets);
    setPreset(pattern.presets[0]);
    setInputs(pattern.variables);
    setVariables({ ...pattern.presets[0] });
  };

  const handlePresetChange = (event) => {
    const preset = presets.find((preset) => preset.name === event.target.value);
    setPreset(preset);
    setVariables({ ...preset });
  };

  const handleVariableChange = (event) => {
    const { name, value } = event.target;
    setVariables({ ...variables, [name]: value });
  };

  const handleDimensionChange = (event) => {
    const { name, value } = event.target;
    setDimensions({ ...dimensions, [name]: value });
  };

  const handleRatioChange = (event) => {
    const ratio = ratios.find((ratio) => ratio.name === event.target.value);
    setDimensions({
      ratio: ratio.name,
      width: ratio.width ? ratio.width : dimensions.width,
      height: ratio.height ? ratio.height : dimensions.height,
    });
  };

  const handleRefresh = () => {
    setIteration(iteration + 1);
  };

  const downloadFile = (dataUrl, filename) => {
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = filename;
    a.click();
  };

  const handleExport = (type) => {
    const svgEl = document.querySelector(".Canvas svg");
    const canvasEl = document.querySelector(".Canvas canvas");
    const options = {
      width: dimensions.width,
      height: dimensions.height,
      pixelRatio: 2,
    };
    const filename = [
      pattern.name,
      stringifyVariables(variables),
      stringifyDimensions(dimensions),
      stringifyTimestamp(new Date()),
    ].join(" ");
    switch (type) {
      case "png":
        setExportState({ type: "png" });
        toPng(canvasEl ?? svgEl, options)
          .then((dataUrl) => {
            downloadFile(dataUrl, filename + ".png");
          })
          .finally(() => setExportState(null));
        break;
      case "svg":
        if (svgEl) {
          setExportState({ type: "svg" });
          toSvg(svgEl, options)
            .then((dataUrl) => {
              downloadFile(dataUrl, filename + ".svg");
            })
            .finally(() => setExportState(null));
        }
        break;
      case "copy":
        // Canvas patterns: write the current frame as a real PNG image to the clipboard
        setExportState({ type: "copy" });
        canvasEl.toBlob((blob) => {
          navigator.clipboard
            .write([new ClipboardItem({ "image/png": blob })])
            .finally(() => setExportState(null));
        });
        break;
      case "url":
        // SVG patterns: write the SVG data URL as text to the clipboard
        setExportState({ type: "url" });
        toSvg(svgEl, options)
          .then((dataUrl) => {
            navigator.clipboard.writeText(dataUrl);
          })
          .finally(() => setExportState(null));
        break;
      case "webm": {
        const fps = 60;
        const durationSecs = Number(variables.duration ?? 5);
        const durationMs = durationSecs * 1000;
        const totalFrames = Math.round((durationMs / 1000) * fps);
        const mimeType =
          ["video/webm;codecs=vp9", "video/webm;codecs=vp8", "video/webm"].find(
            (t) => MediaRecorder.isTypeSupported(t),
          ) ?? "video/webm";
        const stream = canvasEl.captureStream(0);
        const track = stream.getVideoTracks()[0];
        const recorder = new MediaRecorder(stream, { mimeType });
        const chunks = [];
        let frameCount = 0;
        // Countdown: decrement remaining seconds once per second for UI feedback
        setExportState({ type: "webm", remaining: durationSecs });
        const countdown = setInterval(() => {
          setExportState((prev) =>
            prev?.type === "webm"
              ? { type: "webm", remaining: Math.max(0, prev.remaining - 1) }
              : prev,
          );
        }, 1000);
        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) chunks.push(e.data);
        };
        recorder.onstop = () => {
          clearInterval(countdown);
          const blob = new Blob(chunks, { type: mimeType });
          downloadFile(URL.createObjectURL(blob), filename + ".webm");
          recordingRef.current = null;
          setExportState(null);
        };
        recordingRef.current = {
          frameDt: (1000 / fps) * (Number(variables.speed) / 100),
          onFrame: () => {
            track.requestFrame();
            frameCount++;
            if (frameCount >= totalFrames) recorder.stop();
          },
        };
        recorder.start();
        break;
      }
      default:
        break;
    }
  };

  return (
    <div className="App">
      <main className="App-main">
        <Dimensions
          ratios={ratios}
          dimensions={dimensions}
          onRatioChange={handleRatioChange}
          onDimensionChange={handleDimensionChange}
        />
        <Canvas
          key={iteration}
          Output={pattern.Output}
          dimensions={dimensions}
          variables={variables}
          recordingRef={recordingRef}
        />
      </main>
      <aside className="App-aside">
        <Patterns
          pattern={pattern}
          patterns={patterns}
          onPatternChange={handlePatternChange}
        />
        <Presets
          preset={preset}
          presets={presets}
          onPresetChange={handlePresetChange}
        />
        <Inputs
          inputs={inputs}
          variables={variables}
          onVariableChange={handleVariableChange}
        />
        <Actions
          exportOptions={pattern.exportOptions ?? ["png", "svg", "url"]}
          exportState={exportState}
          onExport={handleExport}
          onRefresh={handleRefresh}
        />
      </aside>
    </div>
  );
};

export default App;
